import React, { useEffect, useRef, useState } from "react";
import { PERSONAL_MESSAGE_MAX_LENGTH } from "../../../constants/global";
import { useDebounce } from "../../../debounce";
import { hasXSSCharacters } from "../../../helpers/Utils";
import { CustomizeStepModel, ICustomizeStep } from "../../../models/CustomizeStep.model";
import IGiftCardGif from "../../../models/GiftCardGif.model";
import IGiftCardTemplate from "../../../models/GiftCardTemplate.model";
import { ICustomizeStepProps } from "../../../models/props/CustomizeStepProps.interface";
import DataService from "../../../services/DataService.service";
import { AppSettingContext } from "../../Application";
import LoadingComponent from "../../shared/LoadingComponent";
import MediaUploader from "./MediaUploader";

const CustomizeStep = (props: ICustomizeStepProps) => {
    const { debugMode, labels } = React.useContext(AppSettingContext);

    const [pageModel, setPageModel] = useState<ICustomizeStep>(props.model);
    const [isDirty, setIsDirty] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [useCardTemplate, setUseCardTemplate] = useState(true);
    const [templates, setTemplates] = useState(Array<IGiftCardTemplate>(0));
    const [lastUploadedTemplate, setLastUploadedTemplate] = useState("");

    const [writePersonalMessage, setWritePersonalMessage] = useState(true);

    const [addMedia, setAddMedia] = useState(false);
    const [useGif, setUseGif] = useState(true);
    const [areGifsLoading, setAreGifsLoading] = useState(false);
    const [gifs, setGifs] = useState(Array<IGiftCardGif>(0));
    const [lastUploadedMedia, setLastUploadedMedia] = useState("");

    const checkboxRef = useRef(null)
    const culture = document.documentElement.lang;

    //useEffect(() => {
    //    checkboxRef.current.checked = props.expanded;
    //});

    useEffect(() => {
        setIsLoading(true);

        setPageModel({ ...props.model });
        loadAllTemplates();
    }, []);

    useEffect(() => {
        setIsLoading(true);

        if (pageModel) {
            setIsLoading(false);
            //console.log(pageModel);

            const result = checkIfModelIsDirty();
            setIsDirty(result);
            props.handleDirtyState(result);
        }
    }, [pageModel]);

    useEffect(() => {
        if (templates && templates.length > 0) {
            if (!templates.map(x => x.imageUrl).includes(pageModel.giftCardUrl)) {
                setLastUploadedTemplate(pageModel.giftCardUrl);
                setUseCardTemplate(false);
            }
        }
    }, [templates]);

    useEffect(() => {
        setAreGifsLoading(false);

        setTimeout(() => {
            initSlider();
        }, 600);
    }, [gifs]);

    useEffect(() => {
        setPageModel({ ...pageModel, giftCardUrl: props.model.giftCardUrl });
    }, [props.model.giftCardUrl]);

    useEffect(() => {
        checkboxRef.current.checked = props.expanded;

        if (props.expanded) {
            if (pageModel.mediaUrl && pageModel.mediaUrl.trim()) {
                // the user has already selected media
                setAddMedia(true);

                if (pageModel.mediaUrl.includes("giphy.com")) {
                    loadAllGifs("");
                    handleAddMediaSwitch(true);
                    handleUseGifChange(true);
                } else {
                    setLastUploadedMedia(pageModel.mediaUrl);
                    handleAddMediaSwitch(true);
                    handleUseGifChange(false);
                    props.handleMediaChange(pageModel.mediaUrl);
                }
            }
        }
    }, [props.expanded]);

    const handleExpandCheck = (): void => {
        if (checkboxRef.current.checked) {
            props.handleBeginEdit();

            setTimeout(() => {
                if (addMedia && useGif) {
                    loadAllGifs("");
                }
            }, 200);
        }
    };

    const handleTemplateChange = (templateUrl: string): void => {
        if (templateUrl && templateUrl.trim()) {
            setPageModel({
                ...pageModel,
                giftCardUrl: templateUrl
            });

            props.handleTemplateChange(templateUrl);
        }
    };

    const handleWritePersonalMessageSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setWritePersonalMessage(event.target.checked);
    };

    const handlePersonalMessageChange = (personalMessage: string): void => {
        if (hasXSSCharacters(personalMessage)) {
            return;
        }

        setPageModel({
            ...pageModel,
            personalMessage: personalMessage
        });
    };

    const handleMediaChange = (mediaUrl: string): void => {
        // deselect if already selected
        if (mediaUrl === pageModel.mediaUrl) {
            mediaUrl = "";
        }

        setPageModel({
            ...pageModel, mediaUrl: mediaUrl
        });

        setAddMedia(true);

        props.handleMediaChange(mediaUrl);
    };

    const handleAddMediaSwitch = (addMedia: boolean): void => {
        setAddMedia(addMedia);

        if (addMedia) {
            props.handleMediaChange(pageModel.mediaUrl);
            loadAllGifs("");
        } else {
            props.handleMediaChange("");
        }
    };

    const handleUseGifChange = (useGif: boolean): void => {
        if (useGif) {
            loadAllGifs("");
        }

        setUseGif(useGif);
    };

    const handleGifSearch = useDebounce<React.ChangeEvent<HTMLInputElement>>((event) => {
        loadAllGifs(event.target.value);
    }, 1000);

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        props.handleSubmit({
            giftCardUrl: pageModel.giftCardUrl,
            mediaUrl: pageModel.mediaUrl,
            personalMessage: pageModel.personalMessage,
            isDirty: true,
            userGiftCards: [],
            userMedias: []
        });

        setIsDirty(false);
    };

    //const handlePreview = (event: React.MouseEvent<HTMLButtonElement>): void => {
    //    event.preventDefault();

    //    props.handlePreview(personalMessage);

    //    //DO NOT UPDATE MODEL like we do in handleSaveAndContinue,
    //    // but just propagade model and update Preview component (show draft label)

    //    props.handleSubmit();
    //    page model is already updated
    //    setIsExpanded(false);
    //    update page model to open recipients
    //};

    const loadAllTemplates = (): void => {
        DataService.getGiftCardsTemplates(culture)
            .then(response => {
                if (response) {
                    const _templates = Array<IGiftCardTemplate>(0);

                    response.forEach((item, index) => {
                        _templates.push(item);
                    });

                    setTemplates(_templates);
                }
            })
            .catch(error => {
                console.error(error);
            });
    };

    const loadAllGifs = (searchTerm: string): void => {
        setAreGifsLoading(true);

        DataService.getGiftCardsGifsTemplates(culture, searchTerm)
            .then(response => {
                if (response) {
                    const gifItems = Array<IGiftCardGif>(0);
                    response.forEach((item, index) => {
                        gifItems.push(item);
                    });

                    setGifs(gifItems);
                }
            }).catch(error => {
                console.error(error);
            });
    };

    const initSlider = (): void => {
        if (document.querySelector('.swiper-gif')) {
            // @ts-ignore
            const swiperGif = new Swiper('.swiper-gif', {
                slidesPerView: "auto",
                spaceBetween: 0,
                slidesPerGroup: 2,
                grid: {
                    rows: 2,
                },
                freeMode: true,
                navigation: {
                    nextEl: '.swiper-gif-button-next',
                    prevEl: '.swiper-gif-button-prev',
                },
            });
        }
    };

    const checkIfModelIsDirty = (): boolean => {
        if (!pageModel) {
            return false;
        }

        if (pageModel.giftCardUrl != props.model.giftCardUrl) {
            return true;
        }

        if (pageModel.mediaUrl != props.model.mediaUrl) {
            return true;
        }

        if (pageModel.personalMessage != props.model.personalMessage) {
            return true;
        }

        return false;
    };

    const isValid = (): boolean => {
        return CustomizeStepModel.validateModel(pageModel);
    };

    return (
        <div className="customise box">
            <input ref={checkboxRef}
                defaultChecked={false}
                onChange={handleExpandCheck}
                type="checkbox" name="customise-hide" id="customise-hide" data-customise-box-hide
            />

            <div className={`action-card ${props.isActive ? "" : "inactive"}`} style={{ display: 'grid' }}>
                <h3>{labels.get("GiftCard.App.Steps.Customise")}</h3>

                <div className="edit">
                    {(!props.isSaving && props.isActive) &&
                        <label htmlFor="customise-hide" className="label-fs">
                            <i></i>
                        </label>
                    }
                </div>

                <div className="show">
                    {(props.isSaving) && (
                        <LoadingComponent />
                    )}

                    {(!isLoading && !props.isSaving) &&
                        <div className="slide-content">
                            {debugMode &&
                                <div>
                                    <p>Template Url: {pageModel.giftCardUrl ? pageModel.giftCardUrl.replace(/^.*[\\\/]/, '') : ""}</p>
                                    <p>GIF Url: {pageModel.mediaUrl ? pageModel.mediaUrl.replace(/^.*[\\\/]/, '') : ""}</p>
                                    <p>Message: {pageModel.personalMessage}</p>

                                    <p>Is dirty: {String(isDirty)}</p>
                                    <p>Is valid: {String(isValid())}</p>
                                    <p>Is active: {String(props.isActive)}</p>
                                </div>
                            }

                            {/* -------------------- GIFTCARD TEMPLATES -------------------- */}
                            <div className="card-customization">
                                <h5>{labels.get("GiftCard.App.V2.CustomiseCard.Title")}</h5>
                                <ul className="card-cover">
                                    <li>
                                        <input
                                            type="radio"
                                            name="card-template"
                                            id="card-template"
                                            checked={useCardTemplate}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setUseCardTemplate(true);
                                                }
                                            }}
                                        />
                                        <label htmlFor="card-template">
                                            {labels.get("GiftCard.App.V2.CustomiseCard.Button.CardTemplate")}
                                        </label>
                                    </li>
                                    <li>
                                        <input
                                            type="radio"
                                            name="upload-photo"
                                            id="upload-photo"
                                            checked={!useCardTemplate}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setUseCardTemplate(false);
                                                }
                                            }}
                                        />
                                        <label htmlFor="upload-photo">
                                            {labels.get("GiftCard.App.V2.CustomiseCard.Button.Upload")}
                                        </label>
                                    </li>
                                </ul>

                                {useCardTemplate ? (
                                    <div className="card-templates fadeIn">
                                        {templates.map((template, index) => (
                                            <img
                                                key={`template_${index}`}
                                                alt="Template Image"
                                                className={template.imageUrl === pageModel.giftCardUrl ? "selected" : ""}
                                                src={template.imageUrl}
                                                onClick={() => handleTemplateChange(template.imageUrl)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <MediaUploader
                                        uploaderId={1}
                                        selectedMediaUrl={pageModel.giftCardUrl}
                                        lastUploadedTemplate={lastUploadedTemplate}
                                        isVideoAllowed={false}
                                        imageCropperAspectRatio={1.59}

                                        setLastUploadedTemplate={setLastUploadedTemplate}
                                        handleMediaChange={handleTemplateChange}
                                    />
                                )}
                            </div>
                            {/* ------------------------------------------------------------ */}

                            {/* ------------------------ MAIL MEDIA ------------------------ */}
                            <div className="mail-customization">
                                <h5>{labels.get("GiftCard.App.V2.CustomiseMail.Title")}</h5>
                                <div className="personal-message" style={{ marginTop: '12px' }}>
                                    <div className="message-details" style={{ margin: '0' }}>
                                        {/*<h5>{labels.get("GiftCard.App.V2.CustomiseMail.Subtitle")}</h5>*/}
                                        <input
                                            type="checkbox"
                                            name="personal-message__switch"
                                            id="personal-message__switch"
                                            className="switch-input"
                                            checked={writePersonalMessage}
                                            onChange={handleWritePersonalMessageSwitch}
                                        />
                                        <div className="switch-label-box" style={{ marginTop: '12px' }}>
                                            <p>{labels.get("GiftCard.App.V2.CustomiseMail.Label.PersonalMessage")}</p>
                                            <label htmlFor="personal-message__switch" className="switch">
                                                <span className="switch__slider switch__round"></span>
                                            </label>
                                        </div>
                                        <div className="tab" style={{ marginTop: '0' }}>
                                            <div className="slide-content">
                                                <label htmlFor="personal__message"></label>
                                                <div className="msg">
                                                    <textarea
                                                        name="personal__message"
                                                        id="personal__message"
                                                        maxLength={PERSONAL_MESSAGE_MAX_LENGTH}
                                                        placeholder={labels.get("GiftCard.App.V2.CustomiseMail.Placeholder.PersonalMessage")}
                                                        value={pageModel.personalMessage}
                                                        onChange={(e) => handlePersonalMessageChange(e.target.value)}
                                                    ></textarea>
                                                </div>
                                                <div
                                                    className="remaining-chars"
                                                    style={{ color: `${PERSONAL_MESSAGE_MAX_LENGTH - pageModel.personalMessage.length <= 20 ? "red" : ""}` }}
                                                >
                                                    {PERSONAL_MESSAGE_MAX_LENGTH - pageModel.personalMessage.length}/{PERSONAL_MESSAGE_MAX_LENGTH}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="add-multimedia add-multimedia-gif">
                                        <input
                                            type="checkbox"
                                            name="add-multimedia__switch-gif"
                                            id="add-multimedia__switch-gif"
                                            className="switch-input"
                                            checked={addMedia}
                                            onChange={(e) => handleAddMediaSwitch(e.target.checked)}
                                        />
                                        <div className="switch-label-box">
                                            <p>Add a Photo, Video or GIF</p>
                                            <label htmlFor="add-multimedia__switch-gif" className="switch">
                                                <span className="switch__slider switch__round"></span>
                                            </label>
                                        </div>

                                        {addMedia && (
                                            <>
                                                <ul className="card-cover">
                                                    <li>
                                                        <input
                                                            type="radio"
                                                            name="mail-gif"
                                                            id="mail-gif"
                                                            checked={useGif}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleUseGifChange(true);
                                                                }
                                                            }}
                                                        />
                                                        <label htmlFor="mail-gif">
                                                            {labels.get("GiftCard.App.V2.CustomiseMail.Button.Gif")}
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <input
                                                            type="radio"
                                                            name="mail-media"
                                                            id="mail-media"
                                                            checked={!useGif}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleUseGifChange(false);
                                                                }
                                                            }}
                                                        />
                                                        <label htmlFor="mail-media">
                                                            {labels.get("GiftCard.App.V2.CustomiseMail.Button.Upload")}
                                                        </label>
                                                    </li>
                                                </ul>

                                                {useGif ? (
                                                    <div className="card-templates fadeIn" style={{ display: 'grid' }}>
                                                        <div className="tab" style={{ display: 'grid', gridTemplateRows: '1fr', overflow: 'hidden', transition: 'grid-template-rows .5s', gridColumn: 'span 2', marginBottom: '20px' }}>
                                                            <div className="slide-content">
                                                                <input type="text"
                                                                    name="add-multimedia__search"
                                                                    onChange={handleGifSearch}
                                                                    placeholder={labels.get("GiftCard.App.Steps.Customise.SearchGifs.Placeholder")}
                                                                    className="search-input" />

                                                                <div className="gif-container">
                                                                    <div className="swiper-gif">
                                                                        {areGifsLoading ? (
                                                                            <LoadingComponent />
                                                                        ) : (
                                                                            <div className="swiper-wrapper" style={{ flexDirection: 'column' }}>
                                                                                {gifs.map((gif, index) => (
                                                                                    <div className="swiper-slide" key={index} >
                                                                                        <span
                                                                                            className={(pageModel.mediaUrl && (pageModel.mediaUrl === gif.video || pageModel.mediaUrl === gif.image)) ? "selected" : ""}
                                                                                            style={{ height: 'inherit' }}
                                                                                            onClick={() => handleMediaChange(gif.video.trim() ? gif.video : gif.image)}
                                                                                        >
                                                                                            <img
                                                                                                style={{ height: 'inherit' }}
                                                                                                alt={gif.title}
                                                                                                src={gif.video.trim() ? gif.video : gif.image}
                                                                                            />
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className="swiper-gif-button-prev small-fs">
                                                                        <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M6 11.5L1 6.5L6 1.5" stroke="#9363D9" strokeWidth="1.67" strokeLinecap="round"
                                                                                strokeLinejoin="round" />
                                                                        </svg>
                                                                        {labels.get("GiftCard.App.Steps.Customise.Templates.Btn.Previous")}
                                                                    </div>
                                                                    <div className="swiper-gif-button-next small-fs">
                                                                        {labels.get("GiftCard.App.Steps.Customise.Templates.Btn.Next")}
                                                                        <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M1 1.5L6 6.5L1 11.5" stroke="#9363D9" strokeWidth="1.67" strokeLinecap="round"
                                                                                strokeLinejoin="round" />
                                                                        </svg>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <MediaUploader
                                                        uploaderId={2}
                                                        selectedMediaUrl={pageModel.mediaUrl}
                                                        lastUploadedTemplate={lastUploadedMedia}
                                                        isVideoAllowed={true}
                                                        imageCropperAspectRatio={NaN}

                                                        setLastUploadedTemplate={setLastUploadedMedia}
                                                        handleMediaChange={handleMediaChange}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* ------------------------------------------------------------ */}

                            <div className="add-multimedia">
                                <div className="multimedia-details">
                                    <button
                                        className="btn btn__primary btn__large btn__full_width large-fs"
                                        disabled={!isValid() || (!isDirty && !props.hasRequestToSaveBasket)}
                                        onClick={handleSaveAndContinue}
                                    >
                                        {labels.get("GiftCard.App.Steps.Customise.Btn.Save")}
                                    </button>

                                    {/*<button*/}
                                    {/*    className="btn btn__outlined btn__large btn__full_width large-fs"*/}
                                    {/*    disabled={!isValid()}*/}
                                    {/*    onClick={handlePreview}*/}
                                    {/*>*/}
                                    {/*    {labels.get("GiftCard.App.Steps.Customise.Btn.Preview")}*/}
                                    {/*</button>*/}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    )
};

export default CustomizeStep;